import React, { ButtonHTMLAttributes, forwardRef, memo } from 'react';
import { ArrowLeft, LucideIcon } from 'lucide-react';
import styled, { css } from 'styled-components';

import { mq } from 'components/constants/breakpoints';
import { T } from 'services/ThemeTokens';

/**
 * ТЗ 0121 — общий вид кнопки-значка на телефоне: зона нажатия 40 × 40,
 * значок 20, без рамки, цвета из токенов темы, видимое кольцо фокуса.
 * Миксин — для кнопок, у которых на компьютере свой вид (подписанное
 * «← Назад к списку» панелей переписки): им он нужен только под `mq.mobile`.
 */
export const mobileIconButtonCss = css`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${T.text2};
  cursor: pointer;

  &:hover,
  &:active {
    background: ${T.hover};
    color: ${T.text};
  }

  &:focus-visible {
    outline: 2px solid ${T.focusRing};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    display: block;
    width: 20px;
    height: 20px;
  }
`;

const Button = styled.button<{ $mobileOnly?: boolean }>`
  ${mobileIconButtonCss}

  ${({ $mobileOnly }) =>
    $mobileOnly &&
    css`
      display: none;

      ${mq.mobile} {
        display: inline-flex;
      }
    `}
`;

export interface IMobileIconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Подпись для чтеца обязательна: на кнопке только значок. */
  'aria-label': string;
  icon: LucideIcon;
  /** Видна только на телефоне (крестики полноэкранных окон). */
  mobileOnly?: boolean;
}

/** Кнопка-значок 40 × 40 (крестик, «Назад», поиск) — один вид на телефоне. */
export const MobileIconButton = memo(
  forwardRef<HTMLButtonElement, IMobileIconButtonProps>(
    ({ icon: Icon, mobileOnly, type = 'button', ...rest }, ref) => (
      <Button
        ref={ref}
        // eslint-disable-next-line react/button-has-type
        type={type}
        $mobileOnly={mobileOnly}
        {...rest}
      >
        <Icon size={20} strokeWidth={2} aria-hidden="true" />
      </Button>
    ),
  ),
);

MobileIconButton.displayName = 'MobileIconButton';

export type IBackButtonProps = Omit<IMobileIconButtonProps, 'icon'>;

/** «Назад» шага на телефоне — стрелка влево 20. */
export const BackButton = memo(
  forwardRef<HTMLButtonElement, IBackButtonProps>((props, ref) => (
    <MobileIconButton ref={ref} icon={ArrowLeft} {...props} />
  )),
);

BackButton.displayName = 'BackButton';
